import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'natural-language', label: 'Natural Language Input', level: 1 },
  { id: 'context-awareness', label: 'Context Awareness', level: 1 },
  { id: 'query-generation', label: 'Query Generation', level: 1 },
  { id: 'using-the-generator', label: 'Using the Generator' },
  { id: 'quick-search', label: 'Quick Search Mode', level: 1 },
  { id: 'advanced-mode', label: 'Advanced Mode', level: 1 },
  { id: 'refining-results', label: 'Refining Results', level: 1 },
  { id: 'examples', label: 'Examples' },
  { id: 'example-errors', label: 'Finding Errors', level: 1 },
  { id: 'example-latency', label: 'Latency Analysis', level: 1 },
  { id: 'example-patterns', label: 'Pattern Detection', level: 1 },
  { id: 'example-comparison', label: 'Before & After Deploys', level: 1 },
  { id: 'supported-queries', label: 'Supported Query Types' },
  { id: 'tips', label: 'Tips & Best Practices' },
  { id: 'privacy', label: 'Privacy & Data Handling' },
  { id: 'limitations', label: 'Limitations' },
];

export function AiQueryGeneratorPage() {
  useDocumentMeta({ title: 'AI Query Generator: LogPulse Docs', description: 'Describe what you are looking for in plain English and LogPulse generates the LPQL query for you.' });

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-2">
            Search
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            AI Query Generator
          </h1>
          <P>
            Describe what you are looking for in plain English, and LogPulse generates the LPQL
            query for you. No need to memorize syntax. Just ask.
          </P>
        </div>

        {/* Overview */}
        <Section id="overview" title="Overview">
          <P>
            The AI Query Generator is built into the Log Explorer search bar. It translates
            natural language descriptions into optimized LPQL queries, so you can find the logs
            you need without knowing the exact syntax. Whether you are new to LogPulse or an
            experienced user looking to save time, the generator helps you go from question to
            answer faster.
          </P>
          <P>
            The generator understands your schema, knows which fields are available in your
            environment, and produces queries that take advantage of LPQL features like pipe
            commands, aggregations, and time ranges.
          </P>
          <Callout type="info">
            The AI Query Generator is available on all plans, including the Free tier.
            Queries are processed in real time with no per-query charges.
          </Callout>
        </Section>

        {/* How It Works */}
        <Section id="how-it-works" title="How It Works">
          <P>
            The generator works in three stages: it interprets your natural language input,
            enriches it with context from your environment, and compiles the result into a valid
            LPQL query.
          </P>

          <SubSection id="natural-language" title="Natural Language Input">
            <P>
              You type a question or description in the search bar, prefixed with a forward slash
              or by clicking the AI icon. The input can be anything from a simple phrase like
              "show me errors from the payment service" to a complex request like "compare error
              rates between us-east-1 and eu-west-1 over the last 24 hours, broken down by
              service."
            </P>
            <P>
              The generator supports English and Dutch input. It handles typos, abbreviations,
              and informal phrasing. You do not need to be precise.
            </P>
          </SubSection>

          <SubSection id="context-awareness" title="Context Awareness">
            <P>
              Before generating a query, the AI inspects your environment to understand what is
              available. It considers the following context:
            </P>
            <Table
              headers={['Context', 'Description']}
              rows={[
                ['Field names', 'All indexed fields and their types (string, number, boolean, map).'],
                ['Known values', 'High-frequency values for fields like level, source, namespace, and cluster.'],
                ['Time range', 'The currently selected time window in the Log Explorer.'],
                ['Recent queries', 'Your last 10 queries, used to infer intent and avoid repetition.'],
                ['Schema labels', 'Custom labels and attribute keys defined by your team.'],
              ]}
            />
            <P>
              This context injection is what makes the generator accurate for your specific
              environment. A generic AI model would not know that your team uses "payment-svc"
              as the source name for the payment service, but the LogPulse generator does.
            </P>
          </SubSection>

          <SubSection id="query-generation" title="Query Generation">
            <P>
              The final stage compiles your intent and the environment context into an optimized
              LPQL query. The generator applies several optimization rules:
            </P>
            <P>
              1. It places the most selective filters first to maximize partition pruning in
              ClickHouse.
            </P>
            <P>
              2. It uses field expressions instead of full-text search when a specific field is
              known, which is significantly faster.
            </P>
            <P>
              3. It adds appropriate pipe commands for aggregation, sorting, and limiting when
              the question implies a summary rather than raw log output.
            </P>
            <P>
              4. It infers time ranges from your input (for example, "yesterday" maps to
              earliest=-1d@d latest=@d) and combines them with the currently selected range.
            </P>
          </SubSection>
        </Section>

        {/* Using the Generator */}
        <Section id="using-the-generator" title="Using the Generator">
          <SubSection id="quick-search" title="Quick Search Mode">
            <P>
              The fastest way to use the generator is directly from the search bar. Click the
              sparkle icon (or press /) and type your question. The generated LPQL query appears
              below the input as a preview. Press Enter to execute it, or Tab to insert it into
              the search bar for further editing.
            </P>
            <CodeBlock title="Example: Quick search">
{`> show me all 5xx errors from api-gateway in the last hour

Generated LPQL:
source=api-gateway level=error attributes.status_code>=500 earliest=-1h
| stats count by attributes.status_code
| sort -count`}
            </CodeBlock>
          </SubSection>

          <SubSection id="advanced-mode" title="Advanced Mode">
            <P>
              For more complex queries, open the AI panel by clicking "AI Query Generator" in
              the toolbar or pressing Ctrl+K (Cmd+K on macOS). The panel provides a larger input
              area and shows a step-by-step breakdown of how the query was constructed.
            </P>
            <P>
              In advanced mode you can also:
            </P>
            <P>
              1. Pin context fields: force the generator to always include certain filters
              (for example, always scope to your team's namespace).
            </P>
            <P>
              2. Set output preferences: tell the generator whether you want raw logs, a
              count summary, a timechart, or a table.
            </P>
            <P>
              3. Chain follow-up questions: ask "now break that down by region" and the
              generator modifies the previous query instead of starting from scratch.
            </P>
          </SubSection>

          <SubSection id="refining-results" title="Refining Results">
            <P>
              After a query is generated, you can refine it in two ways:
            </P>
            <P>
              Edit the LPQL directly: the generated query is plain text in the search bar. You
              can adjust filters, add pipe commands, or change the time range manually.
            </P>
            <P>
              Ask a follow-up: type another natural language question and the generator will
              modify the existing query rather than replacing it. For example, after generating a
              query that shows errors, you could say "also include warnings" and the generator
              will update the level filter from level=error to (level=error OR level=warn).
            </P>
            <Callout type="tip">
              If the generated query is not quite right, try being more specific about the field
              names or values you expect. For example, "errors where user_id is usr_abc123" is
              more precise than "errors for user abc."
            </Callout>
          </SubSection>
        </Section>

        {/* Examples */}
        <Section id="examples" title="Examples">
          <P>
            Here are common scenarios showing what you can ask and what the generator produces.
          </P>

          <SubSection id="example-errors" title="Finding Errors">
            <CodeBlock title="Input">
{`> show me all errors from the order service in the last 30 minutes`}
            </CodeBlock>
            <CodeBlock title="Generated LPQL">
{`source=order-service level=error earliest=-30m | head 100`}
            </CodeBlock>
          </SubSection>

          <SubSection id="example-latency" title="Latency Analysis">
            <CodeBlock title="Input">
{`> average response time per endpoint for the api-gateway today`}
            </CodeBlock>
            <CodeBlock title="Generated LPQL">
{`source=api-gateway earliest=-1d@d
| stats avg(attributes.duration_ms) as avg_duration by attributes.endpoint
| sort -avg_duration`}
            </CodeBlock>
          </SubSection>

          <SubSection id="example-patterns" title="Pattern Detection">
            <CodeBlock title="Input">
{`> which services have the most errors this week, grouped by hour`}
            </CodeBlock>
            <CodeBlock title="Generated LPQL">
{`level=error earliest=-7d
| timechart span=1h count by source`}
            </CodeBlock>
          </SubSection>

          <SubSection id="example-comparison" title="Before & After Deploys">
            <CodeBlock title="Input">
{`> compare error rates between 2pm and 3pm yesterday vs today for payment-svc`}
            </CodeBlock>
            <CodeBlock title="Generated LPQL">
{`source=payment-svc level=error
| eval period=if(timestamp >= relative_time(now(), "-1d@d+14h") AND timestamp < relative_time(now(), "-1d@d+15h"), "yesterday", "today")
| stats count by period`}
            </CodeBlock>
            <Callout type="info">
              Time comparison queries use LPQL's relative_time function to calculate exact
              windows. The generator handles timezone conversion based on your account settings.
            </Callout>
          </SubSection>
        </Section>

        {/* Supported Query Types */}
        <Section id="supported-queries" title="Supported Query Types">
          <P>
            The generator can produce the following types of LPQL queries:
          </P>
          <Table
            headers={['Query Type', 'Description', 'Example Prompt']}
            rows={[
              ['Filter', 'Field-level and text-search filters', '"errors from auth-service"'],
              ['Aggregation', 'stats, count, avg, sum, min, max, dc', '"count errors by source"'],
              ['Time chart', 'Time-bucketed visualizations', '"error trend over the last 24h"'],
              ['Top/Rare', 'Most or least common values', '"top 10 slowest endpoints"'],
              ['Table', 'Specific fields as columns', '"show timestamp, source, event for errors"'],
              ['Dedup', 'Deduplicate by field', '"unique error messages per service"'],
              ['Comparison', 'Before/after or A/B analysis', '"compare today vs yesterday"'],
              ['Multi-pipe', 'Chained transformations', '"errors, grouped by source, sorted by count"'],
            ]}
          />
        </Section>

        {/* Tips & Best Practices */}
        <Section id="tips" title="Tips & Best Practices">
          <P>
            1. Be specific about field names when you know them. "Show errors where
            attributes.order_id is ord_123" produces better results than "find order 123."
          </P>
          <P>
            2. Mention the output format you want. Saying "as a timechart" or "in a table"
            tells the generator which pipe commands to use.
          </P>
          <P>
            3. Use follow-up questions to iterate. Start broad ("show me errors") and narrow
            down ("only from us-east-1", "just the last hour", "group by service").
          </P>
          <P>
            4. Review the generated query before running it. The preview shows exactly what will
            be executed, and you can edit it directly if needed.
          </P>
          <P>
            5. If you find a generated query useful, save it as a Saved Search. The generator
            is great for exploration, but recurring queries should be saved for consistency and
            speed.
          </P>
          <Callout type="tip">
            You can also use the generator to learn LPQL. Ask a question in natural language,
            review the generated query, and over time you will start writing LPQL directly.
          </Callout>
        </Section>

        {/* Privacy & Data Handling */}
        <Section id="privacy" title="Privacy & Data Handling">
          <P>
            The AI Query Generator processes your natural language input to produce LPQL queries.
            Here is what you should know about how your data is handled:
          </P>
          <P>
            The query generator receives your natural-language question and a description of your
            schema: field names, types, and a handful of example values per field. Those examples
            are read from your own logs, so treat them as log content rather than as pure
            metadata. Whole log events are not included in the prompt for query generation.
          </P>
          <P>
            AI Investigations are different, and worth being explicit about. There the model runs
            searches against your data and reads the rows that come back, up to 500 per query, so
            it can answer the question you asked. Any field masking your organization has
            configured is applied server-side, before the payload leaves LogPulse.
          </P>
          <P>
            Queries are not stored or used for training. Your input is processed in a stateless
            request-response cycle. There is no persistent memory between sessions beyond the
            recent query history stored locally in your browser.
          </P>
          <P>
            LogPulse uses <strong>Claude</strong> (Opus, Sonnet and Haiku) as the underlying model
            for query generation and AI Investigations, accessed through{' '}
            <strong>Amazon Bedrock</strong>. Bedrock does not retain prompts or responses and does
            not use them to train models. AWS is listed as a sub-processor on our{' '}
            <a href="/docs/compliance" className="text-primary-400 hover:underline">Compliance page</a>
            {' '}and covered by the Data Processing Agreement (DPA).
          </P>
          <Callout type="info">
            Inference runs in <strong>eu-north-1 (Stockholm)</strong>, so nothing sent to the model
            leaves the EU. Organizations that would rather send nothing at all can switch the AI
            features off per workspace.
          </Callout>
        </Section>

        {/* Limitations */}
        <Section id="limitations" title="Limitations">
          <P>
            The AI Query Generator is powerful but not perfect. Here are the known limitations:
          </P>
          <Table
            headers={['Limitation', 'Details']}
            rows={[
              ['Complex joins', 'The generator cannot produce queries that combine data from multiple indexes or data sources in a single query.'],
              ['Regex patterns', 'While the generator can produce basic rex commands, it may struggle with complex regular expressions. Write these manually for best results.'],
              ['Ambiguous prompts', 'Vague inputs like "show me everything" may produce overly broad queries. Be specific about what you are looking for.'],
              ['Custom eval functions', 'The generator knows built-in LPQL functions but may not recognize custom functions defined by your team.'],
              ['Non-English languages', 'Full support for English and Dutch. Other languages may work but are not officially supported.'],
            ]}
          />
          <Callout type="tip">
            If the generator produces an unexpected result, try rephrasing your question or
            breaking it into smaller steps. You can always fall back to writing LPQL directly.
          </Callout>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
